import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Alert, IconButton, TextField, Button, Tooltip, MenuItem, Chip, Divider,
} from '@mui/material';
import { Home, AccountTree, Save, DeleteOutline, Add } from '@mui/icons-material';
import axios from 'axios';
import { useGLAccounts } from '../hooks/useGLAccounts';
import ConfirmDialog from './ConfirmDialog';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TYPE_COLOR = {
  Customer: 'primary', Vendor: 'warning', Employee: 'secondary',
  FixedAsset: 'info', Inventory: 'success', Bank: 'default', General: 'default',
};

const BLANK = { code: '', type: 'Customer', account: '', advanceAccount: '', description: '' };

export default function PostingGroupsPage() {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const [meta, setMeta]       = useState({ types: [], accountRole: {} });
  const [groups, setGroups]   = useState([]);
  const [edits, setEdits]     = useState({});    // id -> { account, advanceAccount, description }
  const [neu, setNeu]         = useState(BLANK);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // group pending delete

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };

  const load = useCallback(async () => {
    try {
      const [m, g] = await Promise.all([
        axios.get(`${API}/api/academics/posting-groups-meta`, { headers: hdrs() }),
        axios.get(`${API}/api/academics/posting-groups`, { headers: hdrs() }),
      ]);
      setMeta(m.data || { types: [], accountRole: {} });
      setGroups(g.data || []);
      const e = {};
      (g.data || []).forEach(x => { e[x._id] = { account: x.account, advanceAccount: x.advanceAccount || '', description: x.description || '' }; });
      setEdits(e);
    } catch { setError('Failed to load posting groups'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // GL accounts appropriate for a posting-group type (Asset for receivables, etc.)
  const accountsFor = (type) => {
    const role = meta.accountRole?.[type];
    const active = glAccounts.filter(a => a.isActive !== false && (a.postingType || 'Posting') === 'Posting');
    if (!role || !role.accountType) return active;
    return active.filter(a => a.accountType === role.accountType);
  };
  const roleLabel = (type) => meta.accountRole?.[type]?.label || 'GL Account';

  const acctMenu = (type) => accountsFor(type).map(a => (
    <MenuItem key={a.accountNo} value={a.accountNo}>{a.accountNo} — {a.accountName}</MenuItem>
  ));

  const addGroup = async () => {
    if (!neu.code.trim()) return setError('Code is required');
    if (!neu.account)     return setError('Pick the account for this posting group');
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/api/academics/posting-groups`, neu, { headers: hdrs() });
      flash(`${neu.code.toUpperCase()} added`);
      setNeu(BLANK);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to add'); }
    finally { setBusy(false); }
  };

  const saveGroup = async (g) => {
    setBusy(true); setError('');
    try {
      await axios.put(`${API}/api/academics/posting-groups/${g._id}`, edits[g._id], { headers: hdrs() });
      flash(`${g.code} saved`);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/api/academics/posting-groups/${confirmDel._id}`, { headers: hdrs() });
      flash(`${confirmDel.code} deleted`);
      setConfirmDel(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
    finally { setBusy(false); }
  };

  const dirty = (g) => {
    const e = edits[g._id] || {};
    return e.account !== g.account || (e.advanceAccount || '') !== (g.advanceAccount || '') || (e.description || '') !== (g.description || '');
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTree color="primary" />
              <Typography variant="h5" color="primary">Posting Groups</Typography>
            </Box>
            <Tooltip title="Back to Setups">
              <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper' }}><Home /></IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One posting group per customer, vendor, employee, etc. The <strong>type</strong> decides
            which control account the group maps — Receivables for Customer, Payables for Vendor, and
            so on. Assign these on the master records so every transaction posts to the right account.
          </Typography>

          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell sx={{ minWidth: 240 }}><strong>Account</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell sx={{ width: 110 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(g => {
                const e = edits[g._id] || { account: g.account, advanceAccount: '', description: '' };
                const set = (k, v) => setEdits(m => ({ ...m, [g._id]: { ...m[g._id], [k]: v } }));
                return (
                  <TableRow key={g._id} hover>
                    <TableCell><Typography variant="body2" fontWeight={700} fontFamily="monospace">{g.code}</Typography></TableCell>
                    <TableCell><Chip size="small" label={g.type} color={TYPE_COLOR[g.type] || 'default'} /></TableCell>
                    <TableCell>
                      <TextField select size="small" fullWidth value={e.account}
                        label={roleLabel(g.type)}
                        onChange={ev => set('account', ev.target.value)}>
                        {e.account && !glAccounts.find(a => a.accountNo === e.account) && (
                          <MenuItem value={e.account}>{e.account} (current)</MenuItem>
                        )}
                        {acctMenu(g.type)}
                      </TextField>
                      {g.type === 'Customer' && (
                        <TextField select size="small" fullWidth value={e.advanceAccount} sx={{ mt: 1 }}
                          label="Advance Account (Liability)"
                          onChange={ev => set('advanceAccount', ev.target.value)}>
                          <MenuItem value=""><em>— none —</em></MenuItem>
                          {glAccounts.filter(a => a.accountType === 'Liability').map(a => (
                            <MenuItem key={a.accountNo} value={a.accountNo}>{a.accountNo} — {a.accountName}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={e.description}
                        onChange={ev => set('description', ev.target.value)} placeholder="—" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Button size="small" variant="contained" startIcon={<Save />}
                          disabled={busy || !dirty(g)} onClick={() => saveGroup(g)}>Save</Button>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setConfirmDel(g)}><DeleteOutline fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {groups.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>No posting groups yet — add one below.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Add posting group</Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="flex-start">
            <TextField size="small" label="Code" placeholder="e.g. DOMESTIC" value={neu.code}
              onChange={e => setNeu(n => ({ ...n, code: e.target.value }))} sx={{ width: 150 }} />
            <TextField select size="small" label="Type" value={neu.type} sx={{ width: 150 }}
              onChange={e => setNeu(n => ({ ...n, type: e.target.value, account: '', advanceAccount: '' }))}>
              {(meta.types || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select size="small" label={roleLabel(neu.type)} value={neu.account} sx={{ width: 240 }}
              onChange={e => setNeu(n => ({ ...n, account: e.target.value }))}>
              {acctMenu(neu.type)}
            </TextField>
            <TextField size="small" label="Description" value={neu.description} sx={{ flex: 1, minWidth: 140 }}
              onChange={e => setNeu(n => ({ ...n, description: e.target.value }))} />
            <Button variant="contained" startIcon={<Add />} onClick={addGroup} disabled={busy} sx={{ mt: 0.5 }}>
              {busy ? 'Adding…' : 'Add'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete posting group?"
        message={confirmDel ? `Remove "${confirmDel.code}" (${confirmDel.type})? Master records still pointing at it will have no control account until reassigned.` : ''}
        confirmLabel="Delete" confirmColor="error" loading={busy}
        onConfirm={doDelete} onCancel={() => setConfirmDel(null)}
      />
    </Box>
  );
}
